import {Big} from "big.js";
import {BillingLine} from "../../../../../domain/in/quote/billing-line";
import {ConsumablePricingItem} from "../../../../../domain/in/quote/pricing-item/consumable-pricing-item";
import {DevicePricingItem} from "../../../../../domain/in/quote/pricing-item/device-pricing-item";
import {PricingItem} from "../../../../../domain/in/quote/pricing-item/pricing-item";
import {EnumPricingItemType} from "../../../../../domain/in/quote/pricing-item/pricing-item-type";
import {QuoteService} from "../../../../../domain/in/quote/quote-service";
import {ServiceLine} from "../../../../../domain/in/quote/service-line";
import {ServiceLineSummary} from "../../../../../domain/out/service-line-summary";
import {getTotalAmountWithCurrency} from "../../../../../utils/string-utils";
import {TranslationServiceInterface} from "../../../../translation-service.interface";
import {SynthesisService} from "../../synthesis.service";
import {FrDiscoCustomGroupSummaryMapper} from "./fr.disco.custom-group-summary.mapper";
import {DeviceConsumableLink} from "../../../../../domain/in/quote/device-consumable-link";

export class FrDiscoSynthesisService extends SynthesisService {

    constructor(protected translationService: TranslationServiceInterface) {
        super(new FrDiscoCustomGroupSummaryMapper(translationService), translationService);
    }

    public async getServiceLineSummary(
        serviceLine: ServiceLine,
        eForceRole: string,
        currency: string,
        view: string,
        locale: string
    ): Promise<ServiceLineSummary> {
        return new ServiceLineSummary(
            serviceLine.code,
            await this.getServiceLabel(serviceLine.code, eForceRole, locale),
            getTotalAmountWithCurrency(serviceLine.appliedPrice, currency, locale),
            getTotalAmountWithCurrency(serviceLine.monthlyAmount, currency, locale),
            getTotalAmountWithCurrency(serviceLine.weeklyAmount, currency, locale),
            view,
            await this.getQuoteServiceSummary(currency, eForceRole, view, locale, serviceLine.newQuoteService),
            await this.getQuoteServiceSummary(currency, eForceRole, view, locale, this.aggregateRenewalPricingItems(this.filterCancelledPricingItems(serviceLine)))
        );
    }

    private filterCancelledPricingItems(serviceLine: ServiceLine): ServiceLine {
        if (!serviceLine?.renewalQuoteService?.customGroups) return serviceLine;

        if (!['SAS', 'CAF', 'FON'].includes(serviceLine.code)) return serviceLine;

        serviceLine.renewalQuoteService.customGroups.forEach(customGroup => {
            if (!customGroup?.pricingItems) return;

            const filteredPricingItems: PricingItem[] = [];

            customGroup.pricingItems.forEach(item => {
                const type = item?.pricingItemType as EnumPricingItemType;
                const deviceQty = Number(item?.userInputs?.quantity ?? 0);
                const consoQty = Number(item?.userInputs?.consumableQuantity ?? 0);
                const franchiseConso = Number(item?.userInputs?.franchiseConso ?? 0);

                if (type === EnumPricingItemType.DEVICE && deviceQty !== 0) {
                    filteredPricingItems.push(item);
                }

                if (type === EnumPricingItemType.CONSUMABLE && !(consoQty === 0 && franchiseConso === 0)) {
                    filteredPricingItems.push(item);
                }
            });

            customGroup.pricingItems = filteredPricingItems;
        });

        return serviceLine;
    }

    private aggregateRenewalPricingItems(serviceLine: ServiceLine): QuoteService {
        const quoteService = serviceLine.renewalQuoteService;
        if ((serviceLine.code !== 'SAS' && serviceLine.code !== 'FON' && serviceLine.code !== 'CAF' && serviceLine.code !== '3D' && serviceLine.code !== 'DASRI') || !quoteService) return quoteService;

        const billingLines = serviceLine.billingLines ?? [];
        const deviceConsumablelinks: DeviceConsumableLink[] = [];

        quoteService.customGroups.forEach(group => {
            const originalPricingItems = (group.pricingItems ?? []).slice().sort(
                (a, b) => Number(a.displayOrder ?? 0) - Number(b.displayOrder ?? 0)
            );
            const aggregatedPricingItems: PricingItem[] = [];
            const consumedConsumables = new Set<PricingItem>();

            for (const item of originalPricingItems) {
                if (item.pricingItemType !== EnumPricingItemType.DEVICE || (item as DevicePricingItem).userInputs.quantity <= 0) {
                    continue;
                }

                const codeElis = (item as DevicePricingItem).device.codeElis;
                const deviceBillingCode = this.findBillingCategoryCode(codeElis, billingLines);

                let finalItem: PricingItem = item;

                if (!deviceBillingCode) {
                    aggregatedPricingItems.push(finalItem);
                    continue;
                }

                const linkedConsumables = originalPricingItems.filter(pi =>
                    pi.pricingItemType === EnumPricingItemType.CONSUMABLE && !consumedConsumables.has(pi) && this.findBillingCategoryCode((pi as ConsumablePricingItem).consumable.codeElis, billingLines) === deviceBillingCode
                );

                if (linkedConsumables.length === 1) {
                    const consumablePricingItem = linkedConsumables[0];
                    deviceConsumablelinks.push(new DeviceConsumableLink(item.id, consumablePricingItem.id));
                    finalItem = this.addConsumableToPricingItem(consumablePricingItem, finalItem);
                    consumedConsumables.add(consumablePricingItem);
                }

                aggregatedPricingItems.push(finalItem);
            }


            // ajout des consumables qui ne sont pas liés aux devices
            for (const pi of originalPricingItems) {
                if (pi.pricingItemType === EnumPricingItemType.CONSUMABLE && !consumedConsumables.has(pi)) {
                    aggregatedPricingItems.push(pi);
                }
            }

            for (const pi of aggregatedPricingItems) {
                const hasDevice = (pi as DevicePricingItem).device;
                const hasConsumable = (pi as ConsumablePricingItem).consumable;

                if (hasDevice && hasConsumable) {
                    pi.pricingItemType = EnumPricingItemType.DEVICE_CONSUMABLE_WITH_SUPP;
                } else if (hasDevice) {
                    pi.pricingItemType = EnumPricingItemType.DEVICE;
                } else if (hasConsumable) {
                    pi.pricingItemType = EnumPricingItemType.CONSUMABLE;
                }
            }

            group.pricingItems = aggregatedPricingItems;
        });

        quoteService.deviceConsumableLinks = deviceConsumablelinks;
        return quoteService;
    }

    private addConsumableToPricingItem(consumable: PricingItem, pricingItem: PricingItem): PricingItem {
        (pricingItem as any).consumable = (consumable as any).consumable;
        (pricingItem as any).consumableExtraPrice = (consumable as any).consumableExtraPrice;
        pricingItem.userInputs = {
            ...pricingItem.userInputs,
            ...consumable.userInputs
        };
        pricingItem.totalAppliedPrice = new Big(pricingItem.totalAppliedPrice || 0).plus(consumable.totalAppliedPrice || 0).toString();
        return pricingItem;
    }

    private findBillingCategoryCode(codeElis: string, billingLines: BillingLine[]): string | undefined {
        if (!codeElis) return undefined;
        return billingLines.find(bl => bl.articleCode === codeElis)?.billingCategoryCode;
    }

}

